import {Axios, getToken, getUserLS, saveUserLS} from '@asocial/fe-utils'
import {useMediaQuery} from '@mui/material'
import {GoogleOAuthProvider} from '@react-oauth/google'
import {AxiosResponse} from 'axios'
import {lazy, Suspense, useEffect, useRef, useState} from 'react'
import {Link, Meta,} from 'react-head'
import {useDispatch} from 'react-redux'
import {Navigate, Route, useNavigate} from 'react-router-dom'
import {
  IS_CHAT_REQUESTS_HIDE,
  IS_INTERESTS_ASKED,
  IS_PHOTO_ASKED,
  IS_PROFILE_CHAT_OPEN_LS,
  setChatRequestsHide,
  setInterestsAsked,
  setPhotoAsked,
  setProfileChatOpen
} from 'entities/app'
import {socket, socketConnect, socketDisconnect} from 'entities/app/socket'
import {
  GetPackages,
  PaymentFailedResponse,
  PaymentStatus,
  PaymentSuccessResponse,
  setCredits,
  setPackageModalOpen,
  setPackages,
  setPaymentModal
} from 'entities/payment'
import {
  ChangeTimezone,
  clearUser,
  GetAboutMe,
  GetConfig,
  setUser,
  setUserStatus,
  useIsAuthenticated,
  useUser
} from 'entities/user'
import {ActivitySocket} from 'features/activitySocket'
import {DialogsSocket} from 'features/dialogsSocket'
import {FreshChat, FreshChatOpenPaymentTag} from 'features/freshChat'
import {Notification} from 'features/notification'
import {PersonalNotification} from 'features/personalNotification'
import {SentryRoutes} from 'features/sentry'
import {OPTICKS_LOCAL_STORAGE_KEY} from 'shared/lib'
import {isMobileSafari} from 'shared/lib/links'
import {initSound} from 'shared/lib/sound'
import {img} from 'shared/mirrors/img'
import {text} from 'shared/mirrors/text'
import {ErrorResponsesType, Me, UserStatus, WalletType} from 'shared/types'
import {ConfirmPayModal} from 'widgets/confirmPayModal'
import {GlobalScrollLayout} from 'widgets/globalScrollLayout'
import {PackageModal} from 'widgets/packageModal'
import {PaymentStatusModal} from 'widgets/paymentStatusModal'
import {PostRegisterModal} from 'widgets/postRegisterModal'
import {TrialModal} from 'widgets/trialModal'
import {VerifyEmailModal} from 'widgets/verifyEmailModal'
import {CreditsPage} from './credits'
import {FavoritesPage} from './favorites'
import {RedirectLinkHandler} from './link/utils'
import {MessagesPage} from './messages'
import {NotificationsPage} from './notifications'
import {PeoplePage} from './people'
import {SearchPeopleMobile} from './people/ui/searchPeopleMobile'
import {AntiTraffickingPolicy} from './privacy/anti-trafficking-policy'
import {ProfilePage} from './profile'
import {ProfileMobile} from './profile/ui/profileMobile'
import {SettingsPage} from './settings'

const AuthPage = lazy(() => import('./auth'))
const HomePage = lazy(() => import('./home'))
const LinkPage = lazy(() => import('./link'))
const CryptoPage = lazy(() => import('./crypto'))
const ThreeDsPage = lazy(() => import('./threeDs'))
const CommunityRules = lazy(() => import('pages/privacy/community-rules'))
const DatingSafety = lazy(() => import('pages/privacy/dating-safety'))
const Policy = lazy(() => import('pages/privacy/policy'))
const TermsOfUse = lazy(() => import('pages/privacy/terms-of-use'))
const CredentialsStorageAgreement = lazy(() => import('pages/privacy/credentials-storage-agreement'))

const maxRetry = 3

//init clarity
const initClarity = async () => {
  const user = getUserLS() as Me
  if (!window.clarity || !user) return

  try {
    await window.clarity('identify', user.profile?.email, user?.id)
  } catch (e) {
    console.log(e)
  }
}

const Routing = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const isAuthenticated = useIsAuthenticated()
  const {status} = useUser()
  const [googleClientId, setGoogleClientId] = useState('')
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const is768p = useMediaQuery('(max-width: 767px)')

  const setWalletHandler = (wallet: WalletType) => {
    dispatch(setCredits(wallet))
  }

  const aboutMeHandler = ({data}: AxiosResponse<Me>) => {
    dispatch(setUser(data))
    saveUserLS(data)

    if (data.status !== UserStatus.ACTIVE) return deactivateUserHandler()
    //check timezone
    if (data.profile.timezone?.toLowerCase() !== Intl.DateTimeFormat().resolvedOptions().timeZone.toLowerCase()) {
      ChangeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone).catch(console.error)
    }

    setWalletHandler(data.wallet)
    dispatch(setUserStatus(data.status))
  }

  const addCreditsHandler = (res: PaymentSuccessResponse) => {
    dispatch(setPaymentModal({isOpen: true, status: PaymentStatus.SUCCESS, processPackage: res.package}))
    //fix for backend
    setTimeout(()=>{
      GetAboutMe().then(aboutMeHandler).catch(console.error)

      GetPackages()
        .then(({data}) => dispatch(setPackages(data.data)))
        .catch(console.error)
    }, 3000)
  }

  const paymentFailedHandler = (res: PaymentFailedResponse) => {
    dispatch(setPaymentModal({isOpen: true, status: PaymentStatus.FAILED, error: res.reason}))
    //open support widget
    setTimeout(() => FreshChatOpenPaymentTag(), 2000)
  }

  const deactivateUserHandler = () => dispatch(setUserStatus(UserStatus.DEACTIVATED))

  useEffect(() => {
    const updateVH = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    updateVH()
    window.addEventListener('resize', updateVH)

    return () => {
      window.removeEventListener('resize', updateVH)
    }
  }, [])

  useEffect(() => {
    //close window after payment in new tab
    if (isMobileSafari()) return

    const url = new URL(location.href)
    const idOrder = url.searchParams.get('idOrder')
    const paymentId = url.searchParams.get('payment_id')
    const orderId = url.searchParams.get('order_id')
    if (idOrder || paymentId && orderId) window.close()
  }, [])

  // Коннект к сокету после авторизации
  useEffect(() => {
    isAuthenticated && socketConnect(dispatch)
    return () => {
      socketDisconnect()
    }
  }, [isAuthenticated])

  useEffect(() => {
    RedirectLinkHandler(dispatch, navigate)

    initClarity().catch(console.error)

    if (isAuthenticated) {
      GetAboutMe().then(aboutMeHandler).catch(console.error)
      socket.on('payment.success', addCreditsHandler)
      socket.on('payment.fail', paymentFailedHandler)
      socket.on('wallet', setWalletHandler)
      document.body.addEventListener('click', initSound, true)
      document.body.addEventListener('touchstart', initSound, true)
    }

    return () => {
      if (!isAuthenticated) return

      socket.off('payment.success', addCreditsHandler)
      socket.off('payment.fail', paymentFailedHandler)
      socket.off('wallet', setWalletHandler)
      document.body.removeEventListener('click', initSound, true)
      document.body.removeEventListener('touchstart', initSound, true)
    }
  }, [isAuthenticated])

  useEffect(() => {
    Axios.interceptors.response.use(
      (response) => response,
      async (error: ErrorResponsesType) => {
        if (error?.status === 401) {
          const originalRequest = error.responseError.config
          if (originalRequest._retry >= maxRetry) {
            dispatch(clearUser())
            location.reload()
          }
          originalRequest._retry = (originalRequest._retry || 0) + 1
          originalRequest.headers['Authorization'] = 'Bearer ' + getToken()

          await new Promise((resolve) => setTimeout(resolve, 1000))
          return Axios(originalRequest)
        }

        if (error?.error === 'insufficient_funds') {
          dispatch(setPackageModalOpen(true))
        }

        return Promise.reject(error)
      }
    )

    const user = getUserLS() as Me
    if (user) {
      dispatch(setUser(user))

      const isProfileChatOpen = localStorage.getItem(IS_PROFILE_CHAT_OPEN_LS)
      isProfileChatOpen && dispatch(setProfileChatOpen(JSON.parse(isProfileChatOpen).value))

      const isChatRequestsHide = localStorage.getItem(IS_CHAT_REQUESTS_HIDE)
      isChatRequestsHide && dispatch(setChatRequestsHide(JSON.parse(isChatRequestsHide).value))

      const isPhotoAsked = localStorage.getItem(IS_PHOTO_ASKED)
      isPhotoAsked && dispatch(setPhotoAsked(JSON.parse(isPhotoAsked).value))

      const isInterestsAsked = localStorage.getItem(IS_INTERESTS_ASKED)
      isInterestsAsked && dispatch(setInterestsAsked(JSON.parse(isInterestsAsked).value))
    } else {
      dispatch(setProfileChatOpen(true))
      dispatch(setChatRequestsHide(false))
      dispatch(setPhotoAsked(false))
    }

    setIsLoading(false)

    if (!isAuthenticated || !isLoading) {
      GetConfig()
        .then(res => setGoogleClientId(res.data.oauth.googleClientId))
        .catch(console.error)
    }

  }, [dispatch, setIsLoading])

  if (isLoading) return <></>

  if (!isAuthenticated) {
    return (
      <>
        <Head />
        <Suspense>
          <GoogleOAuthProvider clientId={googleClientId || '1235124'}>
            <SentryRoutes>
              <Route path='/' element={<HomePage />} />
              <Route path='/auth' element={<AuthPage />} />
              <Route path='/register' element={<AuthPage />} />
              <Route path='/privacy-policy' element={<Policy />} />
              <Route path='/community-rules' element={<CommunityRules />} />
              <Route path='/dating-safety' element={<DatingSafety />} />
              <Route path='/terms-of-use' element={<TermsOfUse />} />
              <Route path='/anti-trafficking-policy' element={<AntiTraffickingPolicy />} />
              <Route path='/credentials-storage-agreement' element={<CredentialsStorageAgreement />} />
              <Route path='/link/*' element={<LinkPage />} />
              <Route path='*' element={<Navigate to='/' />} />
            </SentryRoutes>
          </GoogleOAuthProvider>
        </Suspense>
      </>
    )
  }

  if (status === UserStatus.DEACTIVATED) {
    return (
      <>
        <Head />
        <Suspense>
          <SentryRoutes>
            <Route
              path='/myProfile'
              element={!is768p ? <GlobalScrollLayout ref={wrapperRef}>
                <ProfilePage wrapperRef={wrapperRef} />
              </GlobalScrollLayout> : <ProfileMobile />}
            />
            <Route path='/privacy-policy' element={<Policy />} />
            <Route path='/community-rules' element={<CommunityRules />} />
            <Route path='/dating-safety' element={<DatingSafety />} />
            <Route path='/terms-of-use' element={<TermsOfUse />} />
            <Route path='/anti-trafficking-policy' element={<AntiTraffickingPolicy />} />
            <Route path='/credentials-storage-agreement' element={<CredentialsStorageAgreement />} />
            <Route path='/link/*' element={<LinkPage />} />
            <Route path='*' element={<Navigate to='/myProfile' />} />
          </SentryRoutes>
        </Suspense>
        <FreshChat />
      </>
    )
  }

  return (
    <>
      <Head />
      <Suspense>
        <SentryRoutes>
          <Route
            path='/people'
            element={(
              <GlobalScrollLayout
                ref={wrapperRef}
                childrenTop={is768p ? <SearchPeopleMobile wrapperRef={wrapperRef} /> : null}
                mobilePaddingTop={150}
              >
                <PeoplePage wrapperRef={wrapperRef} />
              </GlobalScrollLayout>
            )}
          />
          <Route path='/messages' element={<MessagesPage />} />
          <Route path='/messages/:id' element={<MessagesPage />} />
          <Route
            path='/favorites'
            element={<GlobalScrollLayout ref={wrapperRef}>
              <FavoritesPage wrapperRef={wrapperRef} />
            </GlobalScrollLayout>}
          />
          <Route path='/credits' element={<CreditsPage />} />
          <Route
            path='/myProfile'
            element={!is768p ? <GlobalScrollLayout ref={wrapperRef}>
              <ProfilePage wrapperRef={wrapperRef} />
            </GlobalScrollLayout> : <ProfileMobile />}
          />
          <Route
            path='/profile/:id'
            element={!is768p ? <GlobalScrollLayout ref={wrapperRef}>
              <ProfilePage wrapperRef={wrapperRef} />
            </GlobalScrollLayout> : <ProfileMobile />}
          />
          <Route path='/settings' element={<GlobalScrollLayout><SettingsPage /></GlobalScrollLayout>} />
          <Route
            path='/notifications'
            element={<GlobalScrollLayout ref={wrapperRef}>
              <NotificationsPage wrapperRef={wrapperRef} />
            </GlobalScrollLayout>}
          />
          <Route path='/privacy-policy' element={<Policy />} />
          <Route path='/community-rules' element={<CommunityRules />} />
          <Route path='/dating-safety' element={<DatingSafety />} />
          <Route path='/terms-of-use' element={<TermsOfUse />} />
          <Route path='/anti-trafficking-policy' element={<AntiTraffickingPolicy />} />
          <Route path='/credentials-storage-agreement' element={<CredentialsStorageAgreement />} />
          <Route path='/crypto-pay/:data' element={<CryptoPage />} />
          <Route path='/threeDs/:data' element={<ThreeDsPage />} />
          <Route path='/link/*' element={<LinkPage />} />
          <Route path='*' element={<Navigate to='/people' />} />
        </SentryRoutes>
      </Suspense>
      <PostRegisterModal />
      <TrialModal />
      <FreshChat />
      <PaymentStatusModal />
      <PackageModal />
      <ConfirmPayModal />
      <DialogsSocket />
      <ActivitySocket />
      <Notification />
      <VerifyEmailModal />
      {is768p ? <PersonalNotification /> : null}
    </>
  )
}

export const Head = () => {
  useEffect(() => {
    const title = document.querySelector('title')
    title && (title.textContent = `${text.main.siteName}.com – Social communication network. Explore emotions & meet people online`)
    //anti fraud for prod
    if (location.href.includes('asocial-team') || location.pathname !== '/auth' &&
      location.pathname !== '/register' && location.pathname !== '/') return

    window.addEventListener('opticksEvent', function(event) {
      // @ts-ignore
      const opticksId = event.detail?.opticksClickId

      if (!opticksId) throw new Error('No opticks event clickId!')

      localStorage.setItem(OPTICKS_LOCAL_STORAGE_KEY, opticksId)
    }, true)

    const url = new URL(location.href)
    const subpublisherId = url.searchParams.get('partner')
    const var1 = url.searchParams.get('t1')
    const var2 = url.searchParams.get('t2')

    const scriptURL = new URL('https://opticksprotection.com/o/c5fbcbeb0e78e0d0')

    subpublisherId && scriptURL.searchParams.append('subpublisher_id', subpublisherId)
    var1 && scriptURL.searchParams.append('var1', var1)
    var2 && scriptURL.searchParams.append('var2', var2)
    const script = document.createElement('script')
    script.src = scriptURL.href
    script.type = 'text/javascript'

    document.head.appendChild(script)
  }, [])

  return (
    <>
      <Meta
        name='description'
        content={`${text.main.siteName} - Free Dating website for singles ❤️ Find new friends with interests and hobbies 🤩 Find real love. Free registration and bonuses. Find a Match Online 🥰 No card - join and start surfing free now`}
      />
      <Link rel='icon' href={img.logo} />
    </>
  )
}

export default Routing
